import { registerActions, getAction } from "../actionRegistry";
import { modal, closeModalLast, closeModal } from "../ui/modal";
import { config } from "../config";
import { getRandomInt } from "../utils/string";
import { SERVER_BASE_URL } from "../lib/server";
import { parseOptions } from "./miscOperations";
import {
  getDomElement,
  setText,
  setHtml,
  setStyle,
  show,
  hide,
  addClass,
  removeClass,
  queryAll,
  fadeIn,
  fadeOut,
  h,
} from "../utils/dom";
import { getSearchImage } from "./searchImage/store";
import {
  last_modal_open,
  modal_open_what,
  openModalCount,
  search_active,
  setSearchActive,
  ismobile,
  setStickyHeaderEnabled,
  searchcache,
  setSearchcache,
} from "../globalState";
import { printSizeSegment, printSizeSegmentInUrl } from "./printSizeFilter";

// Local state (only used in this file)
// (`fsview` removed 2026-08: zoom() assigned it but nothing ever read it. Its only
// apparent reader was a `typeof fsview !== "undefined"` guard on a module-level
// `let`, which is always true.)
let searchtimer: ReturnType<typeof setTimeout> | undefined;
let hdpreviewactive = 0;
/** The live HD-preview <img>, held rather than re-found by id on every move. */
let hdPreviewImg: HTMLImageElement | null = null;

/**
 * `offset` is `number | string` because this is a **dispatched action**: the two
 * kinds of caller disagree about the type and only one of them is checked.
 * `contentLoader.ts` imports it and passes a real `130`; `ApiDocs.tsx` emits
 * `data-args="docs-general,80"`, and actionDelegator splits `data-args` on commas
 * into *strings*, so the declared `number` was a lie at every SSR call site. It
 * happened to work only because `-` coerces — `Number()` here says so explicitly.
 *
 * The declaration is the fix available today; the real one is the typed action
 * contract (plan Phase 4), which makes the emitter and the handler agree.
 */
export function scrollto(id: string, offset: number | string): void {
  const el = getDomElement(id);
  if (!el) return;
  window.scrollTo({
    top: el.getBoundingClientRect().top + window.scrollY - Number(offset),
    behavior: "smooth",
  });
}

/**
 * Toggle sidebar (filters)
 */
export function sidebar(what: string): void {
  if (what === "filter") {
    const lmFiltersOpen = getDomElement<HTMLInputElement>("lm_filters_open");
    if (lmFiltersOpen?.value === "0") {
      setTimeout(function () {
        removeClass("window-main", "window-main");
        addClass("window-main", "window-main-open");
      }, 200);
      setTimeout(function () {
        addClass("collection-buttons", "collection-buttons-sticky-sidebar");
      }, 200);
      lmFiltersOpen.value = "1";
      // #window-sidebar carries `transition: left 200ms` in styles/features/chrome.css
      setStyle("window-sidebar", "left", "40px");
      setText("filters-label", "Hide filters");
    } else {
      removeClass("window-main", "window-main-open");
      addClass("window-main", "window-main");
      if (lmFiltersOpen) lmFiltersOpen.value = "0";
      setStyle("window-sidebar", "left", "-400px");
      removeClass("collection-buttons", "collection-buttons-sticky-sidebar");
      setText("filters-label", "Filters");
    }
  }
}

/** Re-tag every currently-rendered thumbnail from one size class to another. */
function swapThumbClass(from: string, to: string): void {
  for (const el of queryAll<HTMLElement>("." + from)) {
    el.classList.remove(from);
    el.classList.add(to);
  }
}

/**
 * Change view layout (big/small/tiny thumbs)
 */
export function view(new_view: string): void {
  if (getDomElement("layout_bigthumbs")) {
    const lmLayout = getDomElement<HTMLInputElement>("lm_layout");
    if (new_view === "bigthumbs") {
      if (lmLayout) lmLayout.value = "bigthumbs";
      swapThumbClass("load-image-tinythumbs", "load-image-bigthumbs");
      hide("layout_bigthumbs");
      show("layout_smallthumbs", "inline");
      removeClass("window-main", "window-main-small-select");
    }
    if (new_view === "smallthumbs") {
      if (lmLayout) lmLayout.value = "smallthumbs";
      swapThumbClass("load-image-bigthumbs", "load-image-smallthumbs");
      show("layout_tinythumbs", "inline");
      hide("layout_smallthumbs");
      addClass("window-main", "window-main-small-select");
    }
    if (new_view === "tinythumbs") {
      if (lmLayout?.value === "smallthumbs") {
        lmLayout.value = "tinythumbs";
        swapThumbClass("load-image-smallthumbs", "load-image-tinythumbs");
        show("layout_bigthumbs", "inline");
        hide("layout_tinythumbs");
        addClass("window-main", "window-main-small-select");
      } else {
        if (lmLayout) lmLayout.value = "tinythumbs";
        swapThumbClass("load-image-bigthumbs", "load-image-tinythumbs");
        show("layout_bigthumbs", "inline");
        hide("layout_smallthumbs");
        addClass("window-main", "window-main-small-select");
      }
    }
  }
}

/**
 * Review view for specific artist
 */
export function reviewviewartist(artist_id: string): void {
  for (const el of queryAll<HTMLElement>(".review-image-small-" + artist_id)) {
    el.classList.remove("review-image-small");
    el.classList.add("review-image-big");
  }
}

/**
 * Toggle zoom on image
 */
export function zoom(): void {
  const el = getDomElement("view-img");
  if (!el) return;
  // Compares the WHOLE class attribute, not classList.contains — the element is
  // expected to carry exactly one of the two classes.
  if (el.getAttribute("class") === "view-img") {
    el.classList.remove("view-img");
    el.classList.add("view-img-fs");
  } else {
    el.classList.remove("view-img-fs");
    el.classList.add("view-img");
  }
}

/**
 * Hide crop window in metadata submission
 */
export function msCropHide(): void {
  show("ms-window-full");
  hide("ms-window-crop");
}

/**
 * Show crop window in metadata submission
 */
export function msCropShow(): void {
  hide("ms-window-full");
  show("ms-window-crop");
}

/**
 * Find previous and next images in gallery
 */
export function findprevnext(): void {
  let previmg = "";
  let nextimg = "";
  let prevcache = "";
  let found = 0;
  let images_total = 0;
  let image_now = 0;

  const viewIdEl = getDomElement<HTMLInputElement>("view-id");
  const lmActiveEl = getDomElement<HTMLInputElement>("lm_active");

  for (const el of queryAll<HTMLElement>(".load-image")) {
    const checkid = el.id.replace("img-", "");
    if (checkid === viewIdEl?.value) {
      image_now = images_total;
    }
    images_total++;
  }

  const images_left = images_total - image_now;

  if (images_left < 20) {
    if (lmActiveEl?.value === "1") {
      getAction("load")?.();
    }
  }

  for (const el of queryAll<HTMLElement>(".load-image")) {
    const checkid = el.id.replace("img-", "");
    if (found === 1 && nextimg === "") {
      nextimg = checkid;
    }
    if (checkid === viewIdEl?.value) {
      found = 1;
      previmg = prevcache;
    }
    prevcache = checkid;
  }

  const imgPrevEl = getDomElement<HTMLInputElement>("img-prev");
  const imgNextEl = getDomElement<HTMLInputElement>("img-next");

  if (previmg !== "") {
    fadeIn("view-prev");
    if (imgPrevEl) imgPrevEl.value = previmg;
  }
  if (nextimg !== "") {
    fadeIn("view-next");
    if (imgNextEl) imgNextEl.value = nextimg;
    // Two separate inputs, not one URL with "hd2" swapped for "ld": that trick only worked
    // on the 1x rendition names, and S3 paths carry no size token to rewrite.
    const hdcache = getDomElement<HTMLInputElement>("hd-" + nextimg)?.value ?? "";
    const ldcache = getDomElement<HTMLInputElement>("ld-" + nextimg)?.value ?? "";
    if (hdcache || ldcache) {
      setHtml(
        "cache",
        (hdcache ? '<img src="' + hdcache + '">' : "") +
          (ldcache ? '<img src="' + ldcache + '">' : "")
      );
    }
  }
}

/**
 * Navigate to previous image
 */
export function prev(): void {
  const imgPrevEl = getDomElement<HTMLInputElement>("img-prev");
  // No target = nothing to do. Without this the modal opened as bare `view:`, which put
  // `/view/` in the address bar and gave ViewModal `Number(undefined)` = NaN for its id — a
  // request that can only fail, leaving the modal on its spinner. The keyboard handler in
  // eventHandlers.ts has always guarded this; the arrow click did not, and #view-prev can be
  // left visible from an earlier artwork because fadeIn's inline `display` survives a re-render.
  if (!imgPrevEl?.value) return;

  const current_location = window.location.pathname;
  const ida = current_location.split("/");
  let modal_load = "view:" + imgPrevEl.value;
  if (ida[3] === "collection" && ida[4]) {
    modal_load += ":collection:" + ida[4];
  }
  console.log("*** Going to prev " + (imgPrevEl?.value ?? ""));
  modal(modal_load, "full", 1);
}

/**
 * Navigate to next image
 */
export function next(): void {
  // See prev() — same guard, same reason.
  const imgNextEl = getDomElement<HTMLInputElement>("img-next");
  if (!imgNextEl?.value) return;

  const current_location = window.location.pathname;
  const ida = current_location.split("/");
  let modal_load = "view:" + imgNextEl.value;
  if (ida[3] === "collection" && ida[4]) {
    modal_load += ":collection:" + ida[4];
  }
  console.log("*** Going to next " + (imgNextEl?.value ?? ""));
  modal(modal_load, "full", 1);
}

/**
 * Check URL and update modal state
 */
export function urlcheck(): void {
  const current_location = window.location.pathname;
  const ida = current_location.split("/");
  const seg = ida[1] ?? "";
  if (!config.modals[seg]) {
    closeModal(last_modal_open);
  } else {
    if (seg === "view" && modal_open_what === "addlist") {
      closeModalLast();
    } else {
      const current_location_mod = current_location.replace("/", "");
      const modal_str = current_location_mod.replace(new RegExp("/", "g"), ":");
      if (seg === "view" && openModalCount > 0) {
        modal(modal_str, config.modals[seg], 1);
      } else {
        modal(modal_str, config.modals[seg]);
      }
    }
  }
  if (current_location === "/") {
    if (search_active === 1) {
      stopsearch();
    }
  }
}

/**
 * Show search interface
 */
export function showsearch(): void {
  setSearchActive(1);
  hide("front01");
  hide("front02");
  fadeIn("searchstuff");
  fadeIn("searchstuff2");
  hide("footer");
  removeClass("front-search-element", "front-search");
  addClass("front-search-element", "front-search-slim");
  const lmFiltersOpen = getDomElement<HTMLInputElement>("lm_filters_open");
  if (lmFiltersOpen?.value === "0" && ismobile === 0) {
    sidebar("filter");
  }
  getDomElement("front-search")?.focus();
  const lmActive = getDomElement<HTMLInputElement>("lm_active");
  if (lmActive) lmActive.value = "1";
  setTimeout(function () {
    getAction("load")?.();
  }, 500);
  setStickyHeaderEnabled(1);
}

/**
 * Fold the Advanced-box state (features/frontAdvanced island) into the filter
 * item as VALUES: `negative` (plain string), `axes` (JSON string — parseFilters
 * String-coerces nested objects, so it must travel pre-encoded), and the
 * `classic-search` flag the server's vector-mode predicate checks via truthy().
 */
function addAdvancedFields(item: Record<string, string>): void {
  const negative = getDomElement<HTMLInputElement>("lm_adv_negative")?.value.trim();
  if (negative) item.negative = negative;
  const axes = getDomElement<HTMLInputElement>("lm_adv_axes")?.value.trim();
  if (axes) item.axes = axes;
  if (getDomElement<HTMLInputElement>("lm_adv_classic")?.value === "1") {
    item["classic-search"] = "1";
  }
  // Reverse image search. The vector travels as a filter VALUE for the same reason `axes`
  // does — parseFilters String-coerces anything else — and it rides on every page request so
  // paging re-ranks against it without re-embedding. Read from the store rather than a
  // hidden input: see features/searchImage/store.ts.
  const image = getSearchImage();
  if (image) item.imagevec = image.vecB64;

  // "Minimum DPI/PPI" — height, width, unit and DPI collapse into ONE filter value, for the
  // same reason `axes` and `imagevec` do: parseFilters String-coerces anything else. The
  // value IS the URL segment, so a shared link and a live search parse identically.
  const printSize = printSizeSegment();
  if (printSize) item.printsize = printSize;
}

/**
 * Debounced handler for the four print-size controls.
 *
 * Unlike `searchtimed` it re-runs the search ONLY when the control's meaning actually
 * changed. Typing a height with no DPI yet leaves the segment empty, which is what the URL
 * already says, so nothing re-runs — otherwise every keystroke on the way to a complete
 * filter re-fetched the whole grid unfiltered.
 */
export function dpifilter(): void {
  clearTimeout(searchtimer);
  searchtimer = setTimeout(function () {
    if (printSizeSegment() === printSizeSegmentInUrl()) return;
    parseOptions();
  }, 1000);
}

/**
 * Execute search
 */
export function search(): void {
  const frontSearchEl = getDomElement<HTMLInputElement>("front-search");
  const lmFiltersOpen = getDomElement<HTMLInputElement>("lm_filters_open");
  const lmActive = getDomElement<HTMLInputElement>("lm_active");
  const lmFrom = getDomElement<HTMLInputElement>("lm_from");
  const lmFilters = getDomElement<HTMLInputElement>("lm_filters");
  const lmOptions = getDomElement<HTMLInputElement>("lm_options");
  const lmAutoclear = getDomElement<HTMLInputElement>("lm_autoclear");

  if (
    searchcache !== (frontSearchEl?.value ? parseInt(frontSearchEl.value) : 0) ||
    searchcache.toString() !== frontSearchEl?.value
  ) {
    if (frontSearchEl?.value) {
      setSearchActive(1);
      hide("front01");
      hide("front02");
      fadeIn("searchstuff");
      fadeIn("searchstuff2");
      hide("footer");
      removeClass("front-search-element", "front-search");
      addClass("front-search-element", "front-search-slim");
      if (lmFiltersOpen?.value === "0" && ismobile === 0) {
        sidebar("filter");
      }
      setStickyHeaderEnabled(1);
      if (lmActive) lmActive.value = "1";
      if (lmFrom) lmFrom.value = "0";
      setText("search-target", "");
      const jsonObj = [];
      const item: Record<string, string> = {};
      item.q = frontSearchEl.value;
      item.options = lmOptions?.value ?? "";
      addAdvancedFields(item);
      jsonObj.push(item);
      const jsonString = JSON.stringify(jsonObj);
      if (lmFilters) lmFilters.value = jsonString;
      setText("collection-buttons-title", 'Search for "' + frontSearchEl.value + '"');
      getAction("load")?.();
    } else {
      if (lmAutoclear?.value !== "0" || (lmOptions?.value ?? "") !== "" || search_active === 1) {
        // Original code has complex logic here for autoclear
        setSearchActive(1);
        hide("front01");
        hide("front02");
        fadeIn("searchstuff");
        fadeIn("searchstuff2");
        removeClass("front-search-element", "front-search");
        addClass("front-search-element", "front-search-slim");
        if (lmFiltersOpen?.value === "0" && ismobile === 0) {
          sidebar("filter");
        }
        setStickyHeaderEnabled(1);
        if (lmActive) lmActive.value = "1";
        if (lmFrom) lmFrom.value = "0";
        setText("search-target", "");
        const jsonObj = [];
        const item: Record<string, string> = {};
        item.q = frontSearchEl?.value ?? "";
        item.options = lmOptions?.value ?? "";
        addAdvancedFields(item);
        jsonObj.push(item);
        const jsonString = JSON.stringify(jsonObj);
        if (lmFilters) lmFilters.value = jsonString;
        setText("collection-buttons-title", "Search");
        getAction("load")?.();
      }
    }
    setSearchcache(getRandomInt(100000, 100000000000));
  }
}

/**
 * Stop search and return to default view
 */
export function stopsearch(): void {
  setSearchActive(0);
  hide("searchstuff");
  hide("searchstuff2");
  fadeIn("front01");
  fadeIn("front02");
  fadeOut("searchstuff");
  fadeOut("searchstuff2");
  addClass("front-search-element", "front-search");
  removeClass("front-search-element", "front-search-slim");
  const lmFiltersOpen = getDomElement<HTMLInputElement>("lm_filters_open");
  if (lmFiltersOpen?.value === "1") {
    sidebar("filter");
  }
  setStickyHeaderEnabled(0);
  setText("collection-buttons-title", "Search");
  setSearchcache(getRandomInt(100000, 100000000000));
}

/**
 * The text the last dispatched search ran with. `null` until the reader types.
 *
 * Seeded lazily from the input's `defaultValue` — the value SSR rendered, which never
 * changes — so an arrow key, Shift or Tab on an untouched box compares equal and does
 * nothing.
 */
let lastSearchedText: string | null = null;

/**
 * The keyup handler on #front-search: debounce a search, but only for keys that CHANGED the
 * text.
 *
 * `keyup` fires for every key, so arrows, modifiers, Escape and Tab were each queuing a
 * full re-search of the same string a second later. Separate from `searchtimed()` on
 * purpose: that one stays the unconditional "run a search soon" primitive, which is what
 * the image-search button and its trash icon call — they change the query without touching
 * the text, and must not be filtered out by this guard.
 */
export function searchtyped(): void {
  const el = getDomElement<HTMLInputElement>("front-search");
  if (!el) return;
  const previous = lastSearchedText ?? el.defaultValue;
  if (el.value === previous) return;
  lastSearchedText = el.value;
  searchtimed();
}

/**
 * Timed search with debounce
 */
export function searchtimed(): void {
  clearTimeout(searchtimer);
  searchtimer = setTimeout(function () {
    parseOptions();
  }, 1000);
}

/**
 * HD preview - show high-definition preview of artwork
 */
export function hdpreview(id: string, event: MouseEvent): void {
  show("hd-preview");
  show("hd-preview-loader");
  show("hd-preview-close", "inline-block");

  const xCoordinate = event.offsetX;
  const yCoordinate = event.offsetY;
  const viewerRect = getDomElement("viewer-img")?.getBoundingClientRect();
  const objWidth = viewerRect?.width ?? 0;
  const objHeight = viewerRect?.height ?? 0;
  const src = `${SERVER_BASE_URL}/hdcheck?id=${id}&x=${xCoordinate}&y=${yCoordinate}&w=${objWidth}&h=${objHeight}`;

  if (hdpreviewactive === 0) {
    // Built as nodes: the box and the image are bound directly instead of being
    // written as markup and then re-found — the box via a `.hd-preview-box`
    // sweep of the WHOLE document, the image by the `id` this string minted.
    // `hdPreviewImg` is kept so the else-branch below needs no lookup either.
    const container = getDomElement("hd-preview");
    if (!container) return;

    // No id: with the reference held, nothing looks this up. `hd-preview-img`
    // had exactly one reader — the `else` branch below, re-finding the image to
    // change its `src` — and `hdpreviewclose()` wipes the container wholesale.
    const img = document.createElement("img");
    img.style.display = "none";
    img.src = src;
    img.addEventListener("load", () => {
      hide("hd-preview-loader");
      show(img);
    });

    const box = h("div", "hd-preview-box", img);
    box.addEventListener("click", () => hdpreviewclose());
    container.replaceChildren(box);

    hdPreviewImg = img;
    hdpreviewactive = 1;
  } else if (hdPreviewImg) {
    hdPreviewImg.src = src;
  }
}

/**
 * Close HD preview
 */
export function hdpreviewclose(): void {
  setText("hd-preview", "");
  hide("hd-preview-loader");
  hide("hd-preview-close");
  hide("hd-preview");
  hdpreviewactive = 0;
  // The node is detached by the setText above; dropping the reference keeps the
  // two pieces of state from disagreeing about whether a preview is open.
  hdPreviewImg = null;
}

/**
 * Close the current browser window/tab.
 *
 * The print view (`views/pages/Print.tsx`) opens in its own window and emits
 * `data-fn="close"`. Under the old PHP dispatch (`window[fn](...)`) this resolved
 * to the NATIVE `window.close`; when the action registry replaced window dispatch
 * the name stopped resolving and the button silently did nothing. Registering it
 * explicitly restores the behaviour and satisfies the VIEW_ACTIONS boot check.
 */
export function close(): void {
  window.close();
}

registerActions({
  close,
  scrollto,
  sidebar,
  view,
  reviewviewartist,
  msCropShow,
  search,
  stopsearch,
  searchtimed,
  searchtyped,
  dpifilter,
  hdpreviewclose,
});
